import {
  Box,
  Button,
  Card,
  Center,
  ScrollAreaAutosize,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconError404,
  IconLockAccessOff,
} from "@tabler/icons-react";
import type { ErrorCode } from "core";
import { useEffect } from "react";
import { Link, useRouteError } from "react-router-dom";
import { purgeNoanAsyncStorage } from "../asyncStoragePersister";
import { PageMeta } from "../components/Page";
import { isDeployed } from "../config";
import { captureAndReportError } from "../sentry";
import type { TablerIcon } from "../utils/icons";

interface ErrorWithStatus {
  status?: number;
}

const errorCodeToTitle: Record<
  ErrorCode,
  {
    title: string;
    description: string;
    icon: TablerIcon;
  }
> = {
  403: {
    title: "No Access",
    description: "You don't appear to have permission to access this page",
    icon: IconLockAccessOff,
  },
  404: {
    title: "Page not found",
    description: "The page you are looking for does not exist.",
    icon: IconError404,
  },
  500: {
    title: "Something went wrong",
    description:
      "An unexpected error occurred. Please refresh the page or try again in a moment.",
    icon: IconAlertCircle,
  },
} as const;

function isError(err: unknown): err is Error {
  return err instanceof Error;
}

/**
 * Resolves an arbitrary number code into a valid ErrorCode, masking
 * anything the page has no copy for as 500.
 */
function asErrorCode(code: number): ErrorCode {
  return code in errorCodeToTitle ? (code as ErrorCode) : 500;
}

export function ErrorPage({
  code,
  title,
  description,
}: {
  code?: ErrorCode;
  title?: string;
  description?: string;
}) {
  const error = useRouteError();
  const resolvedCode = asErrorCode(
    code ?? (error as ErrorWithStatus)?.status ?? 500,
  );
  const Icon = errorCodeToTitle[resolvedCode].icon;

  useEffect(() => {
    if (isError(error)) {
      /**
       * If there's an error, purge the async storage -- this resolves some cases where
       * broken data makes its way into the cache and prevents reloads from fixing
       * an otherwise already resolved issue.
       */
      purgeNoanAsyncStorage();

      captureAndReportError(error);
    }
  }, [error]);

  function onGoBackToMain() {
    /**
     * Forces a full page refresh, in case the redirection to the error page
     * was due to trying to access content from the browser old cache after a
     * deployment.
     */
    window.location.href = "/";
  }

  return (
    <Center component={Stack} align="center" p="xl" ta="center">
      <PageMeta title={title ?? errorCodeToTitle[resolvedCode].title} />
      <ThemeIcon size={180} variant="transparent" color="dimmed">
        <Icon size={180} stroke={1} />
      </ThemeIcon>
      <Title fw="bold" maw={600}>
        {title ?? errorCodeToTitle[resolvedCode].title}
      </Title>
      <Text c="dimmed" maw={600}>
        {description ?? errorCodeToTitle[resolvedCode].description}
      </Text>

      {!isDeployed && isError(error) && (
        <Card miw={500} maw={800}>
          <ScrollAreaAutosize scrollbars="xy">
            <pre>{error.stack}</pre>
          </ScrollAreaAutosize>
        </Card>
      )}

      <Box mt="md">
        {resolvedCode === 500 ? (
          <Button variant="light" onClick={onGoBackToMain}>
            Return to main page
          </Button>
        ) : (
          <Button variant="light" component={Link} to="/">
            Return to main page
          </Button>
        )}
      </Box>
    </Center>
  );
}
